/**
 * Facility jobs as a pure reducer (ADR 0033, integration plan slice 7a): an egg
 * placed in an incubator is a timed job; once its time has passed it is ready,
 * and claiming it hatches the egg into a teammate. The same shape the server's
 * `timed_jobs` row will have (ADR 0003, `wiki/architecture/backend.md`: lazily
 * resolved, claimed once): roadmap step 3 replaces the store, not this module.
 *
 * - Time is integer seconds passed in as `now`; nothing here reads a clock
 *   (the web shell's stand-in clock is `apps/web/src/clock.ts`, the server's
 *   will be its `Clock` service).
 * - `startHatch` takes one egg of the ship's stock and starts a job of
 *   `hatchMinutes × 60` seconds in a free slot (an incubator).
 * - `jobState` is `incubating` before `readyAt` and `ready` from it.
 * - `claimHatch` refuses a job that is not ready; a ready one leaves the jobs,
 *   and its egg's species joins the roster as a level 1 monster with an
 *   instance id derived from the job (`monster.joined`). A repeated claim of
 *   the same job returns the first result and changes nothing (idempotent:
 *   the server's rule for claims).
 * - Nothing the server will decide (genes, passives beyond the species' first,
 *   rarities) is decided here: an egg of species X hatches into a level 1 X.
 *
 * `partyFromRoster` is the first teammate loop's party: the leader, then the
 * roster's monsters in the order they joined, up to the party size, each with
 * its species' first passive.
 */
import type { Monster } from '@ab/content-schema';
import type { HumanMember, MonsterMember, PartyMember } from '@ab/game-core';

/** A facility's timed job (the server's `timed_jobs` row): an egg hatching in a slot. */
export type TimedJob = {
  readonly id: string;
  readonly kind: 'hatch';
  /** The egg's species (`monster.<id>`). */
  readonly subject: string;
  /** The facility slot it runs in (an incubator's interactable id). */
  readonly slot: string;
  readonly startedAt: number;
  readonly readyAt: number;
};

/** A monster in the ship's roster: its instance id, species and level, when it joined and the job it hatched from. */
export type RosterMonster = { readonly id: string; readonly species: string; readonly level: number; readonly joinedAt: number; readonly job: string };

export type JobsState = {
  /** Eggs aboard, by species, in the order they came aboard. */
  readonly eggs: readonly string[];
  readonly jobs: readonly TimedJob[];
  readonly roster: readonly RosterMonster[];
  /** Each claimed job's teammate (job id → roster instance id): a repeated claim answers from here. */
  readonly claimed: Readonly<Record<string, string>>;
  /** The next job's number (job ids are `job.<n>`). */
  readonly nextJob: number;
};

export type JobState = 'incubating' | 'ready';

export type StartResult =
  | { readonly ok: true; readonly state: JobsState; readonly job: TimedJob }
  | { readonly ok: false; readonly reason: 'no_egg' | 'slot_busy' | 'bad_time' };

export type ClaimResult =
  | { readonly ok: true; readonly state: JobsState; readonly event: { readonly type: 'monster.joined'; readonly monster: RosterMonster } }
  | { readonly ok: false; readonly reason: 'unknown_job' | 'not_ready' | 'bad_time' };

const secondsPerMinute = 60;

export const emptyJobs = (): JobsState => ({ eggs: [], jobs: [], roster: [], claimed: {}, nextJob: 1 });

/** Whole seconds only: a client's milliseconds or a fraction never reach a job. */
const seconds = (n: number): boolean => Number.isSafeInteger(n) && n >= 0;

/** An egg comes aboard (a run that won one: `egg_obtained`). */
export const addEgg = (state: JobsState, species: string): JobsState => ({ ...state, eggs: [...state.eggs, species] });

/** Starts hatching the first egg of `egg`'s species in `slot` at `now`; it is ready `hatchMinutes` later. */
export const startHatch = (state: JobsState, egg: string, slot: string, now: number, hatchMinutes: number): StartResult => {
  if (!seconds(now) || !Number.isSafeInteger(hatchMinutes) || hatchMinutes <= 0) return { ok: false, reason: 'bad_time' };
  const at = state.eggs.indexOf(egg);
  if (at < 0) return { ok: false, reason: 'no_egg' };
  if (state.jobs.some((j) => j.slot === slot)) return { ok: false, reason: 'slot_busy' };
  const job: TimedJob = { id: `job.${state.nextJob}`, kind: 'hatch', subject: egg, slot, startedAt: now, readyAt: now + hatchMinutes * secondsPerMinute };
  const eggs = [...state.eggs.slice(0, at), ...state.eggs.slice(at + 1)];
  return { ok: true, state: { ...state, eggs, jobs: [...state.jobs, job], nextJob: state.nextJob + 1 }, job };
};

/** A job's state at `now`: incubating until `readyAt`, ready from it (lazily resolved: nothing ticks). */
export const jobState = (job: TimedJob, now: number): JobState => (now >= job.readyAt ? 'ready' : 'incubating');

/** Seconds left at `now` (0 once ready). */
export const jobRemaining = (job: TimedJob, now: number): number => Math.max(0, job.readyAt - now);

/** The roster instance id a job's hatchling gets: stable, from the job (`monster.joined` names it). */
export const instanceOf = (job: string): string => `hatchling.${job.slice(job.indexOf('.') + 1)}`;

/**
 * Claims a ready hatch: the job leaves, its egg's species joins the roster at level 1. A job claimed before returns
 * that claim's monster again and leaves the state as it is.
 */
export const claimHatch = (state: JobsState, jobId: string, now: number): ClaimResult => {
  if (!seconds(now)) return { ok: false, reason: 'bad_time' };
  const done = state.claimed[jobId];
  if (done !== undefined) {
    const monster = state.roster.find((m) => m.id === done);
    if (monster !== undefined) return { ok: true, state, event: { type: 'monster.joined', monster } };
  }
  const job = state.jobs.find((j) => j.id === jobId);
  if (job === undefined) return { ok: false, reason: 'unknown_job' };
  if (jobState(job, now) !== 'ready') return { ok: false, reason: 'not_ready' };
  const monster: RosterMonster = { id: instanceOf(job.id), species: job.subject, level: 1, joinedAt: now, job: job.id };
  return {
    ok: true,
    state: { ...state, jobs: state.jobs.filter((j) => j.id !== jobId), roster: [...state.roster, monster], claimed: { ...state.claimed, [jobId]: monster.id } },
    event: { type: 'monster.joined', monster },
  };
};

/**
 * The party a run from the airship starts with (the first teammate loop): the leader, then the roster's monsters in
 * the order they joined, up to `partySize` monsters, each at its level with its species' first passive. A roster
 * monster whose species the content lacks is left out. `rosterIds` are the instance ids of the monsters taken, in
 * party order (the leader is no roster monster): the server keeps them with the run (ADR 0037's `with` edges).
 */
export const rosterParty = (leader: HumanMember, roster: readonly RosterMonster[], monsters: Readonly<Record<string, Monster>>, partySize: number): { party: PartyMember[]; rosterIds: string[] } => {
  const members: MonsterMember[] = [];
  const rosterIds: string[] = [];
  for (const m of roster) {
    if (members.length >= partySize) break;
    const species = monsters[m.species];
    const passive = species?.passivePool[0];
    if (passive === undefined) continue;
    members.push({ monster: m.species, level: m.level, passive });
    rosterIds.push(m.id);
  }
  return { party: [leader, ...members], rosterIds };
};

/** `rosterParty`'s party alone. */
export const partyFromRoster = (leader: HumanMember, roster: readonly RosterMonster[], monsters: Readonly<Record<string, Monster>>, partySize: number): PartyMember[] =>
  rosterParty(leader, roster, monsters, partySize).party;
